// ============================================================
// Camera — smooth follow with bounds
// ============================================================
import { GAME_WIDTH, GAME_HEIGHT, CAMERA_LERP, CAMERA_DEAD_ZONE_X, CAMERA_DEAD_ZONE_Y } from '../constants.js';

export class Camera {
  constructor() {
    this.x = 0;
    this.y = 0;
    this._targetX = 0;
    this._targetY = 0;
    this.worldW = 1280;
    this.worldH = 720;
    this.shake = 0;
  }

  setWorldBounds(w, h) {
    this.worldW = w;
    this.worldH = h;
  }

  /** Point the camera at (cx, cy) immediately */
  snapTo(cx, cy) {
    this.x = this._clampX(cx - GAME_WIDTH / 2);
    this.y = this._clampY(cy - GAME_HEIGHT / 2);
    this._targetX = this.x;
    this._targetY = this.y;
  }

  /** Smoothly follow a physics body */
  follow(body, dt) {
    const targetX = body.x - GAME_WIDTH / 2;
    const targetY = body.y - GAME_HEIGHT / 2 + 60;

    // Dead zone: don't scroll until target moves outside dead zone
    const dx = targetX - this.x;
    const dy = targetY - this.y;

    if (Math.abs(dx) > CAMERA_DEAD_ZONE_X / 2) {
      this._targetX = targetX;
    }
    if (Math.abs(dy) > CAMERA_DEAD_ZONE_Y / 2) {
      this._targetY = targetY;
    }

    // Lerp toward target
    this.x += (this._targetX - this.x) * CAMERA_LERP;
    this.y += (this._targetY - this.y) * CAMERA_LERP;

    // Clamp to world bounds
    this.x = this._clampX(this.x);
    this.y = this._clampY(this.y);
  }

  /** Camera shake (seconds) */
  addShake(s) {
    this.shake = Math.max(this.shake, s);
  }

  getOffset() {
    let sx = 0, sy = 0;
    if (this.shake > 0) {
      this.shake -= 0.016;
      const intensity = this.shake * 14;
      sx = (Math.random() - 0.5) * intensity;
      sy = (Math.random() - 0.5) * intensity;
    }
    return { x: -(this.x + sx), y: -(this.y + sy) };
  }

  /** Apply camera transform to a PIXI container */
  applyTo(container, dt) {
    const offset = this.getOffset();
    container.x = offset.x;
    container.y = offset.y;
  }

  _clampX(x) {
    return Math.max(0, Math.min(x, this.worldW - GAME_WIDTH));
  }
  _clampY(y) {
    return Math.max(0, Math.min(y, this.worldH - GAME_HEIGHT));
  }

  /** Convert screen coords to world coords */
  screenToWorld(sx, sy) {
    return { x: sx + this.x, y: sy + this.y };
  }
}
